import { useCallback, useEffect, useRef, useState } from 'react';

export default function useCanvasArtwork({
  canvasSize,
  layerRef,
  notify,
  onArtworkSelect,
  trRef,
}) {
  const [image, setImage] = useState(null);
  const [textNodes, setTextNodes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const imageUploadRef = useRef(null);

  const addWhiteCover = useCallback(() => {
    const size = Math.min(400, canvasSize.width / 3, canvasSize.height / 3);
    const node = { id: `cover-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: 'eraser', x: (canvasSize.width - size) / 2, y: (canvasSize.height - size) / 2,
      width: size, height: size, rotation: 0 };
    onArtworkSelect?.();
    setTextNodes((current) => [...current, node]);
    setSelectedId(node.id);
  }, [canvasSize, onArtworkSelect]);

  useEffect(() => {
    const transformer = trRef.current;
    if (!transformer) return;
    const node = selectedId && layerRef.current?.findOne(`#text-${selectedId}`);
    transformer.nodes(node ? [node] : []);
    transformer.getLayer()?.batchDraw();
  }, [selectedId, textNodes, layerRef, trRef]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Backspace' && selectedId && !event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) {
        setTextNodes((current) => current.filter((node) => node.id !== selectedId));
        setSelectedId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId]);

  const loadImageSafely = useCallback((src) => {
    // Clear image immediately so nothing is rendered while the new source loads.
    setImage(null);
    const imageElement = new window.Image();
    imageElement.crossOrigin = 'Anonymous';
    imageElement.onload = () => {
      setImage(imageElement);
    };
    imageElement.onerror = () => {
      console.error('❌ Error loading image:', src);
    };
    imageElement.src = src;
  }, []);

  const handleRichTextImage = useCallback((richTextImage) => {
    const scale = Math.min(
      1,
      (canvasSize.width * 0.6) / Math.max(richTextImage.width, 1),
      (canvasSize.height * 0.6) / Math.max(richTextImage.height, 1),
    );
    const imageWidth = Math.max(1, Math.round(richTextImage.width * scale));
    const imageHeight = Math.max(1, Math.round(richTextImage.height * scale));
    const newNode = {
      id: Date.now().toString(),
      image: richTextImage,
      x: canvasSize.width / 2 - imageWidth / 2,
      y: canvasSize.height / 2 - imageHeight / 2,
      width: imageWidth,
      height: imageHeight,
      type: 'image',
    };

    setTextNodes((current) => [...current, newNode]);
    setSelectedId(newNode.id);
  }, [canvasSize.height, canvasSize.width]);

  const handleImageUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      notify('Choose an image file.', 'error');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const uploadedImage = new window.Image();
    uploadedImage.onload = () => {
      handleRichTextImage(uploadedImage);
      URL.revokeObjectURL(objectUrl);
      notify('Image added. Drag it to position it on the page.');
    };
    uploadedImage.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      notify('The image could not be opened.', 'error');
    };
    uploadedImage.src = objectUrl;
  }, [handleRichTextImage, notify]);

  const handleSelect = useCallback((id) => {
    onArtworkSelect?.();
    setSelectedId(id);
  }, [onArtworkSelect]);

  const resetArtwork = useCallback(() => {
    setTextNodes([]);
    setImage(null);
    setSelectedId(null);
  }, []);

  return {
    addWhiteCover,
    handleImageUpload,
    handleRichTextImage,
    handleSelect,
    image,
    imageUploadRef,
    loadImageSafely,
    resetArtwork,
    selectedId,
    setSelectedId,
    setTextNodes,
    textNodes,
  };
}
